// OP-3605 — Perfil do módulo Estoque: espelho de src/domain/acesso.ts do
// backend. A tela obedece à MESMA tabela que o servidor aplica — o menu some
// para quem não pode, e a rota também recusa; é a segunda parte que faz a
// separação valer. Funções puras (testáveis) + um hook que lê o token.
import { useMemo } from 'react'
import { useUser } from '../auth'
import { Capacidade, ESTOQUE_PERM, Perfil } from '../../types/estoque'

/** CSV `usuarios.Permissoes` → capacidades. Uma por código ESLn, sem acúmulo. */
export function perfilDe(permissoesCsv: string | null | undefined): Perfil {
  const codigos = String(permissoesCsv || '').split(',').map((s) => s.trim())
  return (Object.keys(ESTOQUE_PERM) as Capacidade[]).filter((cap) => codigos.includes(ESTOQUE_PERM[cap]))
}

export function pode(perfil: Perfil | null | undefined, cap: Capacidade): boolean {
  return Array.isArray(perfil) && perfil.includes(cap)
}

/** Quem não ATENDE é solicitante para fins de ação — inclusive quem só visualiza. */
export function ehSolicitante(perfil: Perfil): boolean {
  return !pode(perfil, 'ATENDER')
}

export function podeVerSolicitacao(c: { perfil: Perfil; autorId: number | null; usuarioId: number }): boolean {
  if (pode(c.perfil, 'VISUALIZAR')) return true
  return pode(c.perfil, 'SOLICITAR') && c.autorId != null && c.autorId === c.usuarioId
}

/**
 * O que cada tela exige. Tela de leitura exige VISUALIZAR (o mesmo que a rota
 * GET que a alimenta); tela de ação exige a capacidade da ação. Tela não
 * declarada é negada — acrescentar rota sem alçada não pode significar
 * "liberada para todo mundo".
 */
export const ABA_EXIGE: Record<string, Capacidade> = {
  solicitar: 'SOLICITAR',
  minhas: 'SOLICITAR',
  pedido: 'SOLICITAR',
  fila: 'VISUALIZAR',
  separacao: 'ATENDER',
  separar: 'ATENDER',
  remessas: 'VISUALIZAR',
  remessa: 'VISUALIZAR',
  vinculos: 'VISUALIZAR',
  vinculo: 'VISUALIZAR',
  solicitacoes: 'VISUALIZAR',
  posicao: 'VISUALIZAR',
  equipamentos: 'VISUALIZAR',
  equipamento: 'VISUALIZAR',
  razao: 'VISUALIZAR',
  catalogo: 'VISUALIZAR',
  entrada: 'PATRIMONIO',
  transferencias: 'VISUALIZAR',
  transferencia: 'VISUALIZAR',
  inventario: 'VISUALIZAR',
  contagem: 'INVENTARIAR',
  baixas: 'VISUALIZAR',
  // Ver a lista de exceções é leitura; resolver é `OCORRENCIAS` (ESL12), e a
  // tela pede a capacidade da ação no botão, não na porta.
  ocorrencias: 'VISUALIZAR',
  notificacoes: 'NOTIFICACOES',
}

export function podeAbrirAba(perfil: Perfil, aba: string): boolean {
  const exige = ABA_EXIGE[aba]
  if (exige == null) return false
  return pode(perfil, exige)
}

/** Perfil de quem está logado, a partir do claim `permissoes` do JWT. */
export const usePerfilEstoque = (): { perfil: Perfil; pode: (cap: Capacidade) => boolean; usuarioId: number | null } => {
  const user = useUser()
  const perfil = useMemo(() => perfilDe(user?.permissoes), [user?.permissoes])
  return {
    perfil,
    pode: (cap: Capacidade) => pode(perfil, cap),
    usuarioId: user?.id != null ? Number(user.id) : null,
  }
}
